<template>
  <Container
    as="section"
    class="w-full py-16 md:py-24 bg-head-office-05"
    data-component="GrowthPeriodSection">
    <div class="flex flex-col gap-8">
      <Typography
        v-if="sectionTitle"
        as="h2"
        variant="section-title"
        class="text-center">
        {{ sectionTitle }}
      </Typography>

      <!-- Desktop: simple 3xN grid -->
      <div class="hidden md:grid gap-4 md:gap-6 lg:gap-10 md:grid-cols-3">
        <CustomNuxtLink
          v-for="(item, index) in items"
          :key="`${item.title}-${index}`"
          :to="item.link.url"
          class="cursor-pointer flex min-h-19 md:min-h-[90px] w-full items-stretch rounded-2xl bg-head-office-40 text-left transition-colors hover:bg-head-office-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-90">
          <div class="flex h-full w-full">
            <div
              v-if="item.image?.src"
              class="grid place-items-center self-stretch w-19 md:w-[90px] overflow-hidden shrink-0">
              <img
                :src="item.image.src"
                :alt="item.image.altText ?? ''"
                class="w-auto object-contain">
            </div>

            <div class="flex flex-1 flex-col justify-center gap-1 pr-5 py-4">
              <span class="text-[18px] font-bold text-primary-100 lowercase whitespace-pre-line">
                {{ item.title }}
              </span>
              <span class="text-[18px] text-primary-70 whitespace-pre-line">
                {{ item.subtitle }}
              </span>
            </div>
          </div>
        </CustomNuxtLink>
      </div>

      <!-- Mobile: the carousel bleeds out of the content column by exactly one gutter on each side, so the
           track spans the full page width. A centered slide is then exactly one content column wide, which
           lines it up with the blocks above and below it, and the gutter left over on either side shows a
           clear sliver of the previous/next card. Deliberately not 100vw: that includes the scrollbar width,
           which pushes the track — and with it the centered card — off-centre. -->
      <div class="md:hidden grow-period-bleed">
        <Swiper
          :modules="swiperModules"
          slides-per-view="auto"
          :space-between="8"
          :centered-slides="true"
          :pagination="slides.length > 1 ? { el: '.growth-period-pagination', clickable: true } : false">
          <SwiperSlide
            v-for="(slideItems, slideIndex) in slides"
            :key="`growth-slide-${slideIndex}`"
            class="grow-period-slide">
            <div class="flex flex-col gap-3">
              <CustomNuxtLink
                v-for="(item, index) in slideItems"
                :key="`${item.title}-${slideIndex}-${index}`"
                :to="item.link.url"
                class="cursor-pointer flex min-h-19 md:min-h-[90px] w-full items-stretch rounded-2xl bg-head-office-40 text-left transition-colors hover:bg-head-office-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-90">
                <div class="flex h-full w-full">
                  <div
                    class="grid self-stretch w-19 md:w-[90px] overflow-hidden place-items-center">
                    <img
                      :src="item.image.src"
                      :alt="item.image.altText ?? ''"
                      class="w-auto object-contain">
                  </div>

                  <div class="flex flex-1 flex-col justify-center gap-1 pr-5 py-4">
                    <span class="text-[14px] font-bold text-primary-100 lowercase whitespace-pre-line">
                      {{ item.title }}
                    </span>
                    <span class="text-[14px] text-primary-70 whitespace-pre-line">
                      {{ item.subtitle }}
                    </span>
                  </div>
                </div>
              </CustomNuxtLink>
            </div>
          </SwiperSlide>

          <template #container-end>
            <div class="growth-period-pagination mt-4 flex justify-center text-primary-90" />
          </template>
        </Swiper>
      </div>
    </div>
  </Container>
</template>

<script setup lang="ts">
import type { GrowPeriodSectionApiModel, ImageApiModel, LinkApiModel } from '@gro-up/api/corporate'
import { Container, CustomNuxtLink, Typography } from '@gro-up/ui/primitives'
import { Pagination, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed } from 'vue'

import 'swiper/css'
import 'swiper/css/pagination'

type GrowthPeriodItem = {
  title: string
  /** Subtitle such as an age range (e.g. "jonger dan 2 jaar"). */
  subtitle: string 
  image: ImageApiModel
  link: LinkApiModel
}

const props = defineProps<GrowPeriodSectionApiModel>()

const swiperModules = [Pagination, A11y]

const items = computed<GrowthPeriodItem[]>(() => props.cards ?? [])
const sectionTitle = computed<string | null | undefined>(() => props.title)

const slides = computed<GrowthPeriodItem[][]>(() => {
  const groups: GrowthPeriodItem[][] = []
  items.value.forEach((item, index) => {
    const groupIndex = Math.floor(index / 3)
    if (!groups[groupIndex]) {
      groups[groupIndex] = []
    }
    groups[groupIndex].push(item)
  })
  return groups
})
</script>

<style scoped>
/* The bleed pulls the track out of the content column by one gutter on each side; the slide is then sized
   back to exactly one content column, so a centered card aligns with the blocks above and below it. */
.grow-period-bleed {
  --grow-period-gutter: var(--content-gutter, clamp(0.9375rem, 4vw, 3.75rem));
  margin-inline: calc(var(--grow-period-gutter) * -1);
}

:deep(.grow-period-slide) {
  width: calc(100% - (var(--grow-period-gutter) * 2));
}

:deep(.growth-period-pagination .swiper-pagination-bullet) {
  width: 10px;
  height: 10px;
  opacity: 0.6;
  background: #c9b99f;
}

:deep(.growth-period-pagination .swiper-pagination-bullet-active) {
  opacity: 1;
  background: #1f122e;
}
</style>
